// =============================================================================
// MeuDriver — Batch O1: VacationModeCard
// Card para ativar/desativar modo férias na página de configurações do instrutor.
// =============================================================================

import { useState, useEffect } from 'react'
import { Palmtree, Calendar, Info } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useInstructorVacationMode, useToggleVacationMode } from '@/hooks/useVacationMode'

export function VacationModeCard() {
  const { data, isLoading } = useInstructorVacationMode()
  const toggle = useToggleVacationMode()

  const [enabled, setEnabled] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [dirty, setDirty] = useState(false)

  // Sincroniza estado local com dados do servidor
  useEffect(() => {
    if (data) {
      setEnabled(data.vacation_mode)
      setStartDate(data.vacation_start ?? '')
      setEndDate(data.vacation_end ?? '')
      setDirty(false)
    }
  }, [data])

  function handleToggle(value: boolean) {
    setEnabled(value)
    setDirty(true)
  }

  function handleSave() {
    toggle.mutate({
      enabled,
      start: enabled && startDate ? startDate : null,
      end: enabled && endDate ? endDate : null,
    }, {
      onSuccess: () => setDirty(false),
    })
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 rounded-xl" />
          <Skeleton className="h-10 rounded-xl" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Palmtree className="h-5 w-5 text-teal-600" />
          Modo férias
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Toggle principal */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="vacation-toggle" className="text-sm font-medium">
              Ativar modo férias
            </Label>
            <p className="text-xs text-muted-foreground">
              Bloqueia novos agendamentos durante o período informado.
            </p>
          </div>
          <Switch
            id="vacation-toggle"
            checked={enabled}
            onCheckedChange={handleToggle}
            data-testid="vacation-mode-toggle"
          />
        </div>

        {/* Date pickers (apenas quando habilitado) */}
        {enabled && (
          <div className="grid grid-cols-2 gap-4 pt-1">
            <div className="space-y-1.5">
              <Label htmlFor="vacation-start" className="text-xs font-medium text-muted-foreground">
                <Calendar className="inline h-3 w-3 mr-1" />
                Início das férias
              </Label>
              <Input
                id="vacation-start"
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setDirty(true) }}
                data-testid="vacation-start-date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vacation-end" className="text-xs font-medium text-muted-foreground">
                <Calendar className="inline h-3 w-3 mr-1" />
                Retorno
              </Label>
              <Input
                id="vacation-end"
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => { setEndDate(e.target.value); setDirty(true) }}
                data-testid="vacation-end-date"
              />
            </div>
          </div>
        )}

        {/* Aviso informativo quando ativo */}
        {enabled && data?.vacation_mode && !dirty && (
          <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              Novos agendamentos bloqueados
              {data.vacation_end
                ? ` até ${format(parseISO(data.vacation_end), "d 'de' MMMM 'de' yyyy", { locale: ptBR })}.`
                : '.'
              }
            </span>
          </div>
        )}

        {/* Botão salvar */}
        {dirty && (
          <Button
            size="sm"
            onClick={handleSave}
            disabled={toggle.isPending}
            data-testid="vacation-save-btn"
            className="w-full"
          >
            {toggle.isPending ? 'Salvando...' : 'Salvar configuração'}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
